import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { API, getAuthToken } from '../App';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { ScrollArea } from '../components/ui/scroll-area';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import { 
  Play, Lock, ShoppingCart, CheckCircle, Clock, Users, Eye, 
  BookOpen, ArrowLeft, Download, PlayCircle, Star, ChevronRight,
  X, Search, Filter, DollarSign, Gift, Crown, Video, Upload,
  Trash2, Edit, Plus, Settings, BarChart3, User, FileText
} from 'lucide-react';

const CoursesPage = () => {
  const navigate = useNavigate();
  const { courseId } = useParams();
  const [currentUser, setCurrentUser] = useState(null);
  const [courses, setCourses] = useState([]);
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [cart, setCart] = useState([]);
  const [myCourses, setMyCourses] = useState([]);
  const [showCart, setShowCart] = useState(false);
  const [showProfile, setShowProfile] = useState(false);
  const [playingVideo, setPlayingVideo] = useState(null);
  const videoRef = useRef(null);

  const categories = ['IT', 'Business', 'Marketing', 'Design', 'HR', 'Finance', 'Communication', 'Leadership', 'Other'];

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    if (courseId) {
      fetchCourseDetails(courseId);
    }
  }, [courseId]);

  const fetchData = async () => {
    try {
      const token = getAuthToken();
      const config = { headers: { Authorization: `Bearer ${token}` } };

      const [userRes, coursesRes, cartRes, myCoursesRes] = await Promise.all([
        axios.get(`${API}/auth/me`, config),
        axios.get(`${API}/courses`, config),
        axios.get(`${API}/cart`, config).catch(() => ({ data: [] })),
        axios.get(`${API}/profile/courses`, config).catch(() => ({ data: [] }))
      ]);

      setCurrentUser(userRes.data);
      setCourses(coursesRes.data);
      setCart(cartRes.data);
      setMyCourses(myCoursesRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load courses');
    } finally {
      setLoading(false);
    }
  };

  const fetchCourseDetails = async (id) => {
    try {
      const token = getAuthToken();
      const res = await axios.get(`${API}/courses/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSelectedCourse(res.data);
    } catch (error) {
      toast.error('Failed to load course details');
    }
  };

  const handleAddToCart = async (courseId) => {
    try {
      const token = getAuthToken();
      await axios.post(`${API}/cart/add/${courseId}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Added to cart!');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add to cart');
    }
  };

  const handleRemoveFromCart = async (courseId) => {
    try {
      const token = getAuthToken();
      await axios.delete(`${API}/cart/${courseId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Removed from cart');
      fetchData();
    } catch (error) {
      toast.error('Failed to remove from cart');
    }
  };

  const handlePurchase = async (courseId) => {
    try {
      const token = getAuthToken();
      await axios.post(`${API}/courses/${courseId}/purchase`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Purchase successful! You now have access to this course.');
      fetchData();
      if (selectedCourse) {
        fetchCourseDetails(selectedCourse.id);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Purchase failed');
    }
  };

  const handleCheckout = async () => {
    try {
      const token = getAuthToken();
      const res = await axios.post(`${API}/cart/checkout`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Checkout successful! Total: ₹${res.data.total_amount}`);
      setShowCart(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Checkout failed');
    }
  };

  const playVideo = async (video) => {
    if (!selectedCourse?.has_access && !video.is_preview && !selectedCourse?.is_free) {
      toast.error('Please purchase this course to access this content');
      return;
    }
    
    // For PDF, open in new tab
    if (video.content_type === 'pdf' && video.pdf_id) {
      const token = getAuthToken();
      window.open(`${API}/courses/pdf/${video.id}?token=${token}`, '_blank');
      return;
    }
    
    setPlayingVideo(video);
  };

  const filteredCourses = courses.filter(course => {
    const matchesSearch = course.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         course.description.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = categoryFilter === 'all' || course.category === categoryFilter;
    const matchesType = typeFilter === 'all' || 
                       (typeFilter === 'free' && course.is_free) ||
                       (typeFilter === 'paid' && !course.is_free);
    return matchesSearch && matchesCategory && matchesType;
  });

  const isInCart = (courseId) => cart.some(item => item.course_id === courseId);
  const hasAccess = (courseId) => {
    const course = courses.find(c => c.id === courseId);
    return course?.has_access || course?.is_free;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-violet-600"></div>
      </div>
    );
  }

  // Course Detail View
  if (selectedCourse) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
        {/* Video Player Modal */}
        {playingVideo && (
          <Dialog open={!!playingVideo} onOpenChange={() => setPlayingVideo(null)}>
            <DialogContent className="max-w-4xl p-0 overflow-hidden">
              <div className="relative bg-black">
                <button
                  onClick={() => setPlayingVideo(null)}
                  className="absolute top-2 right-2 z-10 p-2 bg-black/50 rounded-full text-white hover:bg-black/70"
                >
                  <X className="h-5 w-5" />
                </button>
                <video
                  ref={videoRef}
                  className="w-full"
                  controls
                  autoPlay
                  controlsList={playingVideo.allow_download ? "" : "nodownload"}
                  onContextMenu={(e) => !playingVideo.allow_download && e.preventDefault()}
                  src={`${API}/courses/videos/stream/${playingVideo.id}?token=${getAuthToken()}`}
                >
                  Your browser does not support the video tag.
                </video>
              </div>
              <div className="p-4 bg-white">
                <h3 className="font-semibold text-lg">{playingVideo.title}</h3>
                {playingVideo.description && (
                  <p className="text-gray-600 mt-1">{playingVideo.description}</p>
                )}
                {playingVideo.allow_download && (
                  <a 
                    href={`${API}/courses/videos/download/${playingVideo.id}?token=${getAuthToken()}`}
                    className="inline-flex items-center gap-2 mt-3 text-violet-600 hover:text-violet-700"
                    download
                  >
                    <Download className="h-4 w-4" />
                    Download Video
                  </a>
                )}
              </div>
            </DialogContent>
          </Dialog>
        )}

        {/* Header */}
        <header className="bg-gradient-to-r from-violet-600 to-purple-600 text-white">
          <div className="max-w-7xl mx-auto px-4 py-4">
            <div className="flex items-center justify-between">
              <button 
                onClick={() => setSelectedCourse(null)}
                className="flex items-center gap-2 text-white/80 hover:text-white"
              >
                <ArrowLeft className="h-5 w-5" />
                Back to Courses
              </button>
              <div className="flex items-center gap-3">
                <button
                  onClick={() => setShowCart(true)}
                  className="relative p-2 hover:bg-white/10 rounded-full"
                >
                  <ShoppingCart className="h-5 w-5" />
                  {cart.length > 0 && (
                    <span className="absolute -top-1 -right-1 h-5 w-5 bg-red-500 rounded-full text-xs flex items-center justify-center">
                      {cart.length}
                    </span>
                  )}
                </button>
                <button
                  onClick={() => setShowProfile(true)}
                  className="p-2 hover:bg-white/10 rounded-full"
                >
                  <User className="h-5 w-5" />
                </button>
              </div>
            </div>
          </div>
        </header>

        {/* Course Content */}
        <div className="max-w-7xl mx-auto px-4 py-8">
          <div className="grid lg:grid-cols-3 gap-8">
            {/* Main Content */}
            <div className="lg:col-span-2 space-y-6">
              {/* Course Header */}
              <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
                {selectedCourse.thumbnail_id ? (
                  <img 
                    src={`${API}/courses/thumbnail/${selectedCourse.thumbnail_id}`}
                    alt={selectedCourse.title}
                    className="w-full h-64 object-cover"
                  />
                ) : (
                  <div className="w-full h-64 bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center">
                    <BookOpen className="h-20 w-20 text-white/50" />
                  </div>
                )}
                <div className="p-6">
                  <div className="flex items-center gap-2 mb-2">
                    <Badge className={selectedCourse.is_free ? "bg-green-100 text-green-700" : "bg-violet-100 text-violet-700"}>
                      {selectedCourse.is_free ? <Gift className="h-3 w-3 mr-1" /> : <Crown className="h-3 w-3 mr-1" />}
                      {selectedCourse.is_free ? 'Free' : 'Premium'}
                    </Badge>
                    <Badge variant="outline">{selectedCourse.category}</Badge>
                  </div>
                  <h1 className="text-2xl font-bold text-slate-800 mb-2">{selectedCourse.title}</h1>
                  <p className="text-gray-600">{selectedCourse.description}</p>
                  
                  <div className="flex items-center gap-6 mt-4 text-sm text-gray-500">
                    <span className="flex items-center gap-1">
                      <Video className="h-4 w-4" />
                      {selectedCourse.total_videos} videos
                    </span>
                    <span className="flex items-center gap-1">
                      <Eye className="h-4 w-4" />
                      {selectedCourse.total_views} views
                    </span>
                  </div>
                </div>
              </div>

              {/* Videos List */}
              <div className="bg-white rounded-2xl shadow-lg p-6">
                <h2 className="text-xl font-bold text-slate-800 mb-4">Course Content</h2>
                <div className="space-y-3">
                  {selectedCourse.videos?.length > 0 ? (
                    selectedCourse.videos.map((video, index) => (
                      <div 
                        key={video.id}
                        onClick={() => playVideo(video)}
                        className={`flex items-center gap-4 p-4 rounded-xl border transition-all cursor-pointer ${
                          (selectedCourse.has_access || selectedCourse.is_free || video.is_preview)
                            ? 'hover:bg-violet-50 hover:border-violet-200'
                            : 'opacity-60 hover:bg-gray-50'
                        }`}
                      >
                        <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                          (selectedCourse.has_access || selectedCourse.is_free || video.is_preview)
                            ? video.content_type === 'pdf' 
                              ? 'bg-red-100 text-red-600' 
                              : 'bg-violet-100 text-violet-600'
                            : 'bg-gray-100 text-gray-400'
                        }`}>
                          {(selectedCourse.has_access || selectedCourse.is_free || video.is_preview) ? (
                            video.content_type === 'pdf' ? (
                              <FileText className="h-6 w-6" />
                            ) : (
                              <PlayCircle className="h-6 w-6" />
                            )
                          ) : (
                            <Lock className="h-5 w-5" />
                          )}
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <span className="text-sm text-gray-400">{index + 1}.</span>
                            <h3 className="font-medium text-slate-800">{video.title}</h3>
                            <Badge className={
                              video.content_type === 'pdf' 
                                ? 'bg-red-100 text-red-700 text-xs' 
                                : 'bg-violet-100 text-violet-700 text-xs'
                            }>
                              {video.content_type === 'pdf' ? 'PDF' : 'Video'}
                            </Badge>
                            {video.is_preview && (
                              <Badge className="bg-green-100 text-green-700 text-xs">Preview</Badge>
                            )}
                          </div>
                          {video.description && (
                            <p className="text-sm text-gray-500 mt-1">{video.description}</p>
                          )}
                        </div>
                        <div className="text-right">
                          <span className="text-xs text-gray-400">{video.views} views</span>
                          {video.allow_download && (
                            <Download className="h-4 w-4 text-gray-400 mt-1" />
                          )}
                        </div>
                      </div>
                    ))
                  ) : (
                    <p className="text-center text-gray-500 py-8">No content yet</p>
                  )}
                </div>
              </div>
            </div>

            {/* Sidebar */}
            <div className="space-y-6">
              {/* Purchase Card */}
              <div className="bg-white rounded-2xl shadow-lg p-6 sticky top-6">
                {!selectedCourse.is_free && (
                  <div className="text-3xl font-bold text-slate-800 mb-4">
                    ₹{selectedCourse.price}
                  </div>
                )}
                
                {selectedCourse.has_access || selectedCourse.is_free ? (
                  <div className="space-y-3">
                    <div className="flex items-center gap-2 text-green-600 p-3 bg-green-50 rounded-xl">
                      <CheckCircle className="h-5 w-5" />
                      <span className="font-medium">
                        {selectedCourse.is_free ? 'Free Course' : 'You own this course'}
                      </span>
                    </div>
                    <Button 
                      className="w-full bg-violet-600 hover:bg-violet-700"
                      onClick={() => selectedCourse.videos?.[0] && playVideo(selectedCourse.videos[0])}
                    >
                      <Play className="h-4 w-4 mr-2" />
                      Start Learning
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-3">
                    <Button 
                      className="w-full bg-violet-600 hover:bg-violet-700"
                      onClick={() => handlePurchase(selectedCourse.id)}
                    >
                      <DollarSign className="h-4 w-4 mr-2" />
                      Buy Now
                    </Button>
                    {!isInCart(selectedCourse.id) ? (
                      <Button 
                        variant="outline"
                        className="w-full"
                        onClick={() => handleAddToCart(selectedCourse.id)}
                      >
                        <ShoppingCart className="h-4 w-4 mr-2" />
                        Add to Cart
                      </Button>
                    ) : (
                      <Button 
                        variant="outline"
                        className="w-full text-red-600 border-red-200 hover:bg-red-50"
                        onClick={() => handleRemoveFromCart(selectedCourse.id)}
                      >
                        <X className="h-4 w-4 mr-2" />
                        Remove from Cart
                      </Button>
                    )}
                  </div>
                )}

                {/* Benefits */}
                {selectedCourse.benefits?.length > 0 && (
                  <div className="mt-6 pt-6 border-t">
                    <h3 className="font-semibold text-slate-800 mb-3">What you'll learn</h3>
                    <ul className="space-y-2">
                      {selectedCourse.benefits.map((benefit, index) => (
                        <li key={index} className="flex items-start gap-2 text-sm text-gray-600">
                          <CheckCircle className="h-4 w-4 text-green-500 mt-0.5 flex-shrink-0" />
                          {benefit}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Course Info */}
                <div className="mt-6 pt-6 border-t space-y-3 text-sm">
                  <div className="flex items-center justify-between">
                    <span className="text-gray-500">Videos</span>
                    <span className="font-medium">{selectedCourse.total_videos}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-gray-500">Category</span>
                    <span className="font-medium">{selectedCourse.category}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-gray-500">Total Views</span>
                    <span className="font-medium">{selectedCourse.total_views}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Course Listing View
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
      {/* Cart Modal */}
      <Dialog open={showCart} onOpenChange={setShowCart}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ShoppingCart className="h-5 w-5" />
              Your Cart
            </DialogTitle>
          </DialogHeader>
          <div className="py-4">
            {cart.length === 0 ? (
              <p className="text-center text-gray-500 py-8">Your cart is empty</p>
            ) : (
              <div className="space-y-4">
                {cart.map(item => (
                  <div key={item.id} className="flex items-center gap-4 p-3 border rounded-xl">
                    {item.course?.thumbnail_id ? (
                      <img 
                        src={`${API}/courses/thumbnail/${item.course.thumbnail_id}`}
                        alt={item.course?.title}
                        className="w-20 h-14 object-cover rounded-lg"
                      />
                    ) : (
                      <div className="w-20 h-14 bg-gradient-to-br from-violet-500 to-purple-600 rounded-lg flex items-center justify-center">
                        <BookOpen className="h-6 w-6 text-white/50" />
                      </div>
                    )}
                    <div className="flex-1">
                      <h4 className="font-medium text-slate-800">{item.course?.title}</h4>
                      <p className="text-violet-600 font-semibold">₹{item.course?.price}</p>
                    </div>
                    <button
                      onClick={() => handleRemoveFromCart(item.course_id)}
                      className="p-2 text-red-500 hover:bg-red-50 rounded-full"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                ))}
                <div className="pt-4 border-t">
                  <div className="flex items-center justify-between mb-4">
                    <span className="font-semibold">Total</span>
                    <span className="text-xl font-bold text-violet-600">
                      ₹{cart.reduce((sum, item) => sum + (item.course?.price || 0), 0)}
                    </span>
                  </div>
                  <Button 
                    className="w-full bg-violet-600 hover:bg-violet-700"
                    onClick={handleCheckout}
                  >
                    Checkout
                  </Button>
                </div>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Profile Modal */}
      <Dialog open={showProfile} onOpenChange={setShowProfile}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <User className="h-5 w-5" />
              My Courses
            </DialogTitle>
          </DialogHeader>
          <div className="py-4">
            {myCourses.length === 0 ? (
              <p className="text-center text-gray-500 py-8">You haven't enrolled in any courses yet</p>
            ) : (
              <div className="grid gap-4 max-h-96 overflow-y-auto">
                {myCourses.map(course => (
                  <div 
                    key={course.id} 
                    className="flex items-center gap-4 p-4 border rounded-xl hover:bg-violet-50 cursor-pointer transition-colors"
                    onClick={() => {
                      setShowProfile(false);
                      fetchCourseDetails(course.id);
                    }}
                  >
                    {course.thumbnail_id ? (
                      <img 
                        src={`${API}/courses/thumbnail/${course.thumbnail_id}`}
                        alt={course.title}
                        className="w-24 h-16 object-cover rounded-lg"
                      />
                    ) : (
                      <div className="w-24 h-16 bg-gradient-to-br from-violet-500 to-purple-600 rounded-lg flex items-center justify-center">
                        <BookOpen className="h-8 w-8 text-white/50" />
                      </div>
                    )}
                    <div className="flex-1">
                      <h4 className="font-medium text-slate-800">{course.title}</h4>
                      <div className="flex items-center gap-2 mt-1">
                        <Badge className={
                          course.access_type === 'purchased' ? 'bg-violet-100 text-violet-700' :
                          course.access_type === 'granted' ? 'bg-blue-100 text-blue-700' :
                          'bg-green-100 text-green-700'
                        }>
                          {course.access_type === 'purchased' ? 'Purchased' :
                           course.access_type === 'granted' ? 'Granted Access' : 'Free'}
                        </Badge>
                        <span className="text-xs text-gray-500">
                          {course.total_videos || 0} videos
                        </span>
                      </div>
                    </div>
                    <ChevronRight className="h-5 w-5 text-gray-400" />
                  </div>
                ))}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Header */}
      <header className="bg-gradient-to-r from-violet-600 to-purple-600 text-white">
        <div className="max-w-7xl mx-auto px-4 py-6">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <button 
                onClick={() => navigate('/candidate')}
                className="p-2 hover:bg-white/10 rounded-full"
              >
                <ArrowLeft className="h-5 w-5" />
              </button>
              <div>
                <h1 className="text-2xl font-bold">Courses</h1>
                <p className="text-white/70 text-sm">Learn and grow with our courses</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={() => setShowCart(true)}
                className="relative p-2 hover:bg-white/10 rounded-full"
                data-testid="cart-btn"
              >
                <ShoppingCart className="h-5 w-5" />
                {cart.length > 0 && (
                  <span className="absolute -top-1 -right-1 h-5 w-5 bg-red-500 rounded-full text-xs flex items-center justify-center">
                    {cart.length}
                  </span>
                )}
              </button>
              <button
                onClick={() => setShowProfile(true)}
                className="p-2 hover:bg-white/10 rounded-full"
                data-testid="profile-btn"
              >
                <User className="h-5 w-5" />
              </button>
            </div>
          </div>

          {/* Search and Filters */}
          <div className="flex flex-wrap gap-3">
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-white/50" />
              <Input
                placeholder="Search courses..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 bg-white/10 border-white/20 text-white placeholder:text-white/50"
              />
            </div>
            <Select value={categoryFilter} onValueChange={setCategoryFilter}>
              <SelectTrigger className="w-40 bg-white/10 border-white/20 text-white">
                <SelectValue placeholder="Category" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Categories</SelectItem>
                {categories.map(cat => (
                  <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={typeFilter} onValueChange={setTypeFilter}>
              <SelectTrigger className="w-32 bg-white/10 border-white/20 text-white">
                <SelectValue placeholder="Type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                <SelectItem value="free">Free</SelectItem>
                <SelectItem value="paid">Paid</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </header>

      {/* Course Grid */}
      <main className="max-w-7xl mx-auto px-4 py-8">
        {filteredCourses.length === 0 ? (
          <div className="text-center py-16">
            <BookOpen className="h-16 w-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-600">No courses found</h3>
            <p className="text-gray-500 mt-2">Try adjusting your search or filters</p>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredCourses.map(course => (
              <Card 
                key={course.id}
                className="border-0 shadow-lg overflow-hidden hover:shadow-xl transition-all cursor-pointer group"
                onClick={() => fetchCourseDetails(course.id)}
                data-testid={`course-card-${course.id}`}
              >
                <div className="relative">
                  {course.thumbnail_id ? (
                    <img 
                      src={`${API}/courses/thumbnail/${course.thumbnail_id}`}
                      alt={course.title}
                      className="w-full h-48 object-cover group-hover:scale-105 transition-transform duration-300"
                    />
                  ) : (
                    <div className="w-full h-48 bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center">
                      <BookOpen className="h-16 w-16 text-white/30" />
                    </div>
                  )}
                  {/* Badges */}
                  <div className="absolute top-3 left-3 flex gap-2">
                    <Badge className={course.is_free ? "bg-green-500 text-white" : "bg-violet-600 text-white"}>
                      {course.is_free ? 'Free' : `₹${course.price}`}
                    </Badge>
                  </div>
                  {course.has_access && !course.is_free && (
                    <div className="absolute top-3 right-3">
                      <Badge className="bg-emerald-500 text-white">
                        <CheckCircle className="h-3 w-3 mr-1" />
                        Owned
                      </Badge>
                    </div>
                  )}
                </div>
                <CardContent className="p-5">
                  <Badge variant="outline" className="mb-2">{course.category}</Badge>
                  <h3 className="font-semibold text-lg text-slate-800 mb-2 line-clamp-2 group-hover:text-violet-600 transition-colors">
                    {course.title}
                  </h3>
                  <p className="text-sm text-gray-500 line-clamp-2 mb-3">{course.description}</p>
                  <div className="flex items-center justify-between text-sm text-gray-400">
                    <span className="flex items-center gap-1">
                      <Video className="h-4 w-4" />
                      {course.total_videos} videos
                    </span>
                    <span className="flex items-center gap-1">
                      <Users className="h-4 w-4" />
                      {course.total_views || 0} views
                    </span>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </main>
    </div>
  );
};

export default CoursesPage;
